import { Spacing } from '@persona-id/design-tokens';
import isEqual from 'lodash/fp/isEqual';
import pluralize from 'pluralize';
import { ComponentProps, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useId } from 'react-aria';
import styled from 'styled-components';

import SearchInput, { OmniSearchProps } from 'dashboard/components-lib/SearchInput';
import {
  DateRangeFilter,
  MultiSelectAltFilter,
  MultiSelectFilter,
  MultiSelectLegacyFilter,
  SelectFilter,
} from 'dashboard/components-lib/TableControls/filters';
import TableControlsContext from 'dashboard/components-lib/TableControls/TableControlsContext';
import TableViews, { TableViewsProps } from 'dashboard/components-lib/TableControls/TableViews';
import { ActiveFilter, FilterConfig } from 'dashboard/components-lib/TableControls/types';
import Badge from 'design-system/components/Badge';
import Button from 'design-system/components/Button';
import DropdownMenu from 'design-system/components/DropdownMenu/DropdownMenu';
import Select, { Header as SelectHeader, Option } from 'design-system/components/Select';
import Stack from 'design-system/components/Stack';

const SegmentedControl = styled.div`
  display: grid;
  grid-template-columns: min-content minmax(auto, max-content);

  > *:first-child:not(:last-child) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

interface FilterProps {
  config?: FilterConfig;
}

const Filter = ({ config }: FilterProps) => {
  const { activeFilters, setActiveFilters } = useContext(TableControlsContext);

  let filterComponent = null;

  const updateConfigState = (hasValue: boolean) => {
    const filter = activeFilters.find((filter) => filter.config?.id === config?.id);
    if (filter) {
      filter.isConfiguring = !hasValue;
      setActiveFilters(activeFilters);
    }
  };

  if (config) {
    switch (config.type) {
      case 'select':
        filterComponent = (
          <SelectFilter
            filterConfig={{
              ...config,
              onChange: (val) => {
                updateConfigState(val !== '' && val != null);
                config.onChange(val);
              },
            }}
          />
        );
        break;
      case 'multiselect':
        filterComponent = (
          <MultiSelectFilter
            filterConfig={{
              ...config,
              onChange: (val) => {
                updateConfigState(val.length > 0);
                config.onChange(val);
              },
            }}
          />
        );
        break;
      case 'multiselect-alt':
        filterComponent = (
          <MultiSelectAltFilter
            filterConfig={{
              ...config,
              onChange: (val, isSelectAll) => {
                updateConfigState(val.length > 0 || !!isSelectAll);
                if (config.onChange) {
                  config.onChange(val, isSelectAll);
                }
              },
            }}
          />
        );
        break;
      case 'multiselect-legacy':
        filterComponent = (
          <MultiSelectLegacyFilter
            filterConfig={{
              ...config,
              onChange: (val) => {
                updateConfigState(val.length > 0);
                config.onChange(val);
              },
            }}
          />
        );
        break;
      case 'daterange':
        filterComponent = (
          <DateRangeFilter
            filterConfig={{
              ...config,
              onChange: (val) => {
                updateConfigState(val != null);
                config.onChange(val);
              },
            }}
          />
        );
        break;
      case 'custom':
        filterComponent = config.filterElement;
        break;
    }
  }

  // id is an internal value attached to the filter config. htmlId is applied to the HTML element.
  return (
    <SegmentedControl id={config?.htmlId} role='fieldset'>
      <FilterSelection value={config?.id} />
      {filterComponent}
    </SegmentedControl>
  );
};

interface FilterSelectionProps {
  value?: string;
}

const FilterSelection = ({ value }: FilterSelectionProps) => {
  const { filters, activeFilters, setActiveFilters } = useContext(TableControlsContext);

  const options = filters
    ?.filter((filter) => {
      const activeIds = activeFilters.map(({ config }) => config?.id);
      return filter.id === value || !activeIds.includes(filter.id);
    })
    .filter((filter) => {
      // Skip if doesn't have a group or if it's the current filter
      if (!filter.group || filter.id === value) {
        return true;
      }

      // Check if there's already an active filter with the same group
      return !activeFilters.some(
        (activeFilter) =>
          activeFilter.config?.group === filter.group && activeFilter.config?.id !== value,
      );
    })
    .map((filter) => (
      <Option key={`filters_${filter.id}`} value={`${filter.id}`}>
        {filter.name}
      </Option>
    ));

  if (!options || options.length === 0) {
    return null;
  }

  const onChange = (newVal: string) => {
    const config = filters.find((filter) => filter.id === newVal);
    const prevConfig = filters.find((filter) => filter.id === value);

    if (config) {
      if (!value) {
        const newActiveFilters = activeFilters.map((activeFilter) => {
          if (!activeFilter.config) {
            return {
              ...activeFilter,
              config,
            };
          }

          return activeFilter;
        });

        setActiveFilters(newActiveFilters);

        return;
      }

      const newActiveFilters = activeFilters.map((activeFilter) => {
        if (activeFilter.config?.id === value) {
          switch (prevConfig?.type) {
            case 'multiselect':
            case 'multiselect-alt':
            case 'multiselect-legacy':
              if (prevConfig.onChange) {
                prevConfig.onChange([]);
              }
              break;
            case 'select':
              prevConfig.onChange('');
              break;
            case 'custom':
              prevConfig.onDelete();
              break;
            case 'daterange':
              prevConfig.onChange(null);
              break;
          }

          return {
            ...activeFilter,
            config,
            isConfiguring: true,
          };
        }

        return activeFilter;
      });

      setActiveFilters(newActiveFilters);

      if (config.onFilterSelect) {
        config.onFilterSelect();
      }
    }
  };

  const onDelete = () => {
    const config = filters.find((filter) => filter.id === value);

    const newActiveFilters = activeFilters.filter(
      (activeFilter) => activeFilter.config?.id !== config?.id,
    );

    switch (config?.type) {
      case 'multiselect':
      case 'multiselect-alt':
      case 'multiselect-legacy':
        if (config.onChange) {
          config.onChange([]);
        }
        break;
      case 'daterange':
        config.onChange(null);
        break;
      case 'select':
        config.onChange('');
        break;
      case 'custom':
        config.onDelete();
        break;
    }

    setActiveFilters(newActiveFilters);
  };

  return (
    <Select
      width='auto'
      minWidth={120}
      placeholder='Select filter type'
      onValueChange={onChange}
      value={value}
      header={
        <SelectHeader
          title='Filter type'
          menuTriggerLabel='Filter actions'
          menuItems={
            <>
              <DropdownMenu.Item onClick={onDelete}>Delete filter</DropdownMenu.Item>
            </>
          }
        />
      }
      portal
      zIndex='auto'
    >
      {options}
    </Select>
  );
};

const FiltersContainer = styled.div`
  width: 100%;
  display: flex;
  gap: ${Spacing[2]};
  flex-wrap: wrap;
`;

const getHighestId = (activeFilters: ActiveFilter[]) => {
  return activeFilters.reduce((maxId, filter) => {
    return filter.id > maxId ? filter.id : maxId;
  }, 0);
};

const getActiveFilters = (filters: FilterConfig[], activeFilters: ActiveFilter[] = []) => {
  let currentIndex = getHighestId(activeFilters);

  return filters
    .filter((filter) => {
      switch (filter.type) {
        case 'multiselect':
        case 'multiselect-alt':
        case 'multiselect-legacy':
          if (filter.value.length === 0) {
            return false;
          }
          break;
        case 'daterange':
          if (!filter.value.startDate && !filter.value.endDate) {
            return false;
          }
          break;
        case 'select':
          if (!filter.value) {
            return false;
          }
          break;
        case 'custom':
          if (!filter.hasValue) {
            return false;
          }
          break;
      }

      return true;
    })
    .map((filter) => {
      const active = activeFilters.find((activeFilter) => activeFilter.config?.id === filter.id);

      return {
        id: active ? active.id : (currentIndex += 1),
        config: filter,
        isConfiguring: false,
      };
    })
    .sort((a, b) => a.id - b.id);
};

const Filters = (props: ComponentProps<typeof FiltersContainer>) => {
  const { filters, activeFilters, setActiveFilters } = useContext(TableControlsContext);

  useEffect(() => {
    // Merge new state & filter configs with active filters
    const updatedCurrentFilters = activeFilters.map((filter) => {
      const thisFilter = filters.find((f) => filter.config?.id === f.id);

      if (thisFilter?.type === 'custom') {
        if (filter.config?.type === 'custom') {
          filter.config.filterElement = thisFilter.filterElement;
          return filter;
        }

        return filter;
      }

      if (filter.config && filter.config.type !== 'custom' && thisFilter) {
        filter.config.value = thisFilter.value;

        if (
          (filter.config.type === 'multiselect' || filter.config.type === 'multiselect-alt') &&
          filter.config.options
        ) {
          if (thisFilter.type === 'multiselect' || thisFilter.type === 'multiselect-alt') {
            filter.config.options = thisFilter.options;
          }
        }

        if (filter.config.type === 'multiselect-legacy' && filter.config.selectOptionValues) {
          if (thisFilter.type === 'multiselect-legacy') {
            filter.config.selectOptionValues = thisFilter.selectOptionValues;
          }
        }

        if (
          filter.config.type === 'select' &&
          filter.config.options &&
          thisFilter.type === 'select'
        ) {
          filter.config.options = thisFilter.options;
        }
      }

      return filter;
    });

    const currentFilterIds = activeFilters.map((filter) => filter.config?.id);

    const newFilters = getActiveFilters(
      filters.filter((filter) => !currentFilterIds.includes(filter.id)),
      activeFilters,
    );

    setActiveFilters([...updatedCurrentFilters, ...newFilters]);
  }, [filters]);

  const hasMaxFilters = activeFilters.length === filters.length;
  const hasNewFilter = activeFilters.some((filter) => !filter.config);

  return (
    <FiltersContainer {...props}>
      {activeFilters.map((filter, index) => {
        return <Filter key={`filter_${index}`} config={filter.config} />;
      })}
      <Button
        variant='basic'
        width='auto'
        type='button'
        disabled={hasMaxFilters || hasNewFilter}
        onClick={() => {
          setActiveFilters([
            ...activeFilters,
            { id: getHighestId(activeFilters) + 1, isConfiguring: true },
          ]);
        }}
      >
        Add filter
      </Button>
    </FiltersContainer>
  );
};

const ControlBar = styled.div`
  width: 100%;
  display: flex;
  gap: ${Spacing[2]};
`;

const ControlBarActions = styled.div`
  display: flex;
  gap: ${Spacing[2]};
`;

interface TableControlsProps extends TableViewsProps {
  searchProps: OmniSearchProps;
  filters: FilterConfig[];
  defaultShowFilters?: boolean;
  className?: string;
  tableActions?: ReactNode;
  propertiesButton?: ReactNode;
  isLoading?: boolean;

  /**
   * Whether to display table views tab bar
   */
  showViews?: boolean;
}

const TableControls = ({
  filters,
  searchProps,
  defaultShowFilters,
  className,
  tableActions,
  propertiesButton,
  isLoading,
  showViews,
  canEditViews,
  onSelectView,
  views,
  currentView,
  onAddView,
  onViewNameChange,
  onDuplicateView,
  onDeleteView,
  onSaveView,
  isViewDirty,
}: TableControlsProps) => {
  const filtersId = useId();
  const enabledFilters = useMemo(
    // Memoize otherwise this value will change upon opening the filters
    () => filters.filter((filter) => filter.enabled !== false),
    [filters],
  );
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>(
    getActiveFilters(enabledFilters),
  );
  // Show filters initially if there are active filters or if the defaultShowFilters prop is true.
  const [showFilters, setShowFilters] = useState(
    typeof defaultShowFilters !== 'undefined' ? defaultShowFilters : activeFilters.length > 0,
  );

  useEffect(() => {
    // Update active filters if they've changed externally. Leave any unsaved filters alone.
    const currentActiveFilterIds = activeFilters
      .filter((f) => f.config && !f.isConfiguring)
      .map((f) => f.config?.id);
    const nextActiveFilterIds = getActiveFilters(enabledFilters, activeFilters)
      .filter((f) => f.config)
      .map((f) => f.config?.id);

    if (!isEqual(currentActiveFilterIds, nextActiveFilterIds)) {
      setActiveFilters(getActiveFilters(enabledFilters, activeFilters));
    }
  }, [enabledFilters]);

  return (
    <TableControlsContext.Provider
      value={{
        filters: enabledFilters,
        activeFilters,
        setActiveFilters,
      }}
    >
      <Stack css={{ marginBottom: Spacing[2] }} className={className}>
        {showViews && (
          <TableViews
            views={views}
            onSelectView={onSelectView}
            currentView={currentView}
            onAddView={onAddView}
            onDeleteView={onDeleteView}
            onDuplicateView={onDuplicateView}
            onViewNameChange={onViewNameChange}
            onSaveView={onSaveView}
            isViewDirty={isViewDirty}
            canEditViews={canEditViews}
          />
        )}
        <ControlBar>
          <SearchInput
            css={`
              flex-grow: 1;
            `}
            title={null}
            size='md'
            {...searchProps}
          />
          {tableActions && <ControlBarActions>{tableActions}</ControlBarActions>}
          <Button
            variant='basic'
            width='auto'
            onClick={() => setShowFilters(!showFilters)}
            active={showFilters}
            disabled={isLoading}
            aria-label={showFilters ? 'Hide filters' : 'Show filters'}
            aria-expanded={showFilters}
            aria-controls={filtersId}
          >
            Filter
            {activeFilters.length > 0 && (
              <Badge
                variant='active'
                css={{ marginLeft: Spacing[1] }}
                aria-label={`${activeFilters.length} ${pluralize('filter', activeFilters.length)} applied`}
              >
                {activeFilters.length}
              </Badge>
            )}
          </Button>
          {propertiesButton}
        </ControlBar>
        {showFilters && <Filters id={filtersId} />}
      </Stack>
    </TableControlsContext.Provider>
  );
};

export default TableControls;
